import React, { useCallback, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Cropper from 'react-easy-crop';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, 
  Image as ImageIcon, Link as LinkIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight,
  Undo, Redo, Minus, MousePointer2, Type,
  ShoppingCart, ThumbsUp, ThumbsDown, LayoutGrid, Sparkles, Crop, Check, X
} from 'lucide-react';

// CTA Button styles
const CTA_STYLES = {
  primary: { bg: '#E63946', text: '#FFFFFF', label: 'Buy Now (Red)' },
  secondary: { bg: '#2A9D8F', text: '#FFFFFF', label: 'Get Deal (Green)' },
  amazon: { bg: '#FF9900', text: '#111', label: 'Amazon Style' },
  gradient: { bg: 'linear-gradient(135deg, #E63946 0%, #F4A261 100%)', text: '#FFFFFF', label: 'Gradient' },
  outline: { bg: 'transparent', text: '#E63946', border: '#E63946', label: 'Outline' }
};

// Affiliate block templates
const AFFILIATE_BLOCKS = {
  productCard: {
    label: 'Product Card',
    icon: ShoppingCart,
    description: 'Showcase a single product with image, price, and CTA'
  },
  comparisonTable: {
    label: 'Comparison Table',
    icon: LayoutGrid,
    description: 'Compare multiple products side by side'
  },
  prosConsBox: {
    label: 'Pros & Cons',
    icon: ThumbsUp,
    description: 'Highlight advantages and disadvantages'
  },
  dealHighlight: {
    label: 'Deal Highlight',
    icon: Sparkles,
    description: 'Featured deal with discount badge'
  }
};

/**
 * TipTap Rich Text Editor with Affiliate Content Blocks
 * Features: H1-H4, formatting, affiliate blocks, image cropping
 */
export const RichTextEditor = ({ 
  content = '', 
  onChange, 
  placeholder = 'Start writing your content...',
  onImageUpload
}) => {
  const [showCtaModal, setShowCtaModal] = useState(false);
  const [ctaData, setCtaData] = useState({ text: 'Buy Now', url: '', style: 'primary', newTab: true });
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showBlocksModal, setShowBlocksModal] = useState(false);
  const [showProsConsModal, setShowProsConsModal] = useState(false);
  const [prosConsData, setProsConsData] = useState({ pros: ['', '', ''], cons: ['', '', ''] });
  const [showProductModal, setShowProductModal] = useState(false);
  const [productData, setProductData] = useState({
    name: '', price: '', originalPrice: '', image: '', url: '', rating: '4.5', badge: 'Best Value'
  });
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: { class: 'article-image' },
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'article-link', rel: 'noopener noreferrer nofollow' },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'article-table' },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: { class: 'article-table-header' },
      }),
      TableCell.configure({
        HTMLAttributes: { class: 'article-table-cell' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'editor-content prose prose-lg max-w-none focus:outline-none min-h-[400px] px-6 py-4',
      },
    },
  });

  // Load content when editing
  useEffect(() => {
    if (editor && content) {
      const currentText = editor.getText().trim();
      const isEditorEmpty = !currentText || editor.getHTML() === '<p></p>';
      if (isEditorEmpty && content.trim().length > 0) {
        setTimeout(() => editor.commands.setContent(content, false), 50);
      }
    }
  }, [editor, content]);

  // Image cropping functions
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = new window.Image();
    image.src = imageSrc;
    await new Promise(resolve => image.onload = resolve);
    
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 675;
    const ctx = canvas.getContext('2d');
    
    ctx.drawImage(
      image,
      pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
      0, 0, 1200, 675
    );
    
    return new Promise(resolve => {
      canvas.toBlob(blob => {
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg', 0.85);
    });
  };

  const handleCropConfirm = async () => {
    if (cropImage && croppedAreaPixels) {
      const croppedUrl = await getCroppedImg(cropImage, croppedAreaPixels);
      if (editor) {
        editor.chain().focus().setImage({ src: croppedUrl }).run();
      }
      setShowCropModal(false);
      setCropImage(null);
    }
  };

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL (affiliate links supported):', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = () => {
        setCropImage(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, []);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 4, cols: 4, withHeaderRow: true }).run();
  }, [editor]);

  // Insert CTA Button
  const insertCtaButton = () => {
    if (!editor || !ctaData.text || !ctaData.url) return;
    const style = CTA_STYLES[ctaData.style];
    const buttonHtml = `
      <div class="cta-wrapper" contenteditable="false">
        <a href="${ctaData.url}" ${ctaData.newTab ? 'target="_blank" rel="noopener noreferrer nofollow"' : 'rel="nofollow"'} 
           class="cta-button" style="background: ${style.bg}; color: ${style.text}; ${style.border ? `border: 2px solid ${style.border};` : ''}">
          ${ctaData.text}
        </a>
      </div>`;
    editor.chain().focus().insertContent(buttonHtml).run();
    setShowCtaModal(false);
    setCtaData({ text: 'Buy Now', url: '', style: 'primary', newTab: true });
  };

  // Insert Pros/Cons Box
  const insertProsConsBox = () => {
    if (!editor) return;
    const pros = prosConsData.pros.filter(p => p.trim());
    const cons = prosConsData.cons.filter(c => c.trim());
    
    const html = `
      <div class="pros-cons-box" contenteditable="false">
        <div class="pros-section">
          <div class="pros-header"><span class="icon">✓</span> Pros</div>
          <ul class="pros-list">
            ${pros.map(p => `<li>${p}</li>`).join('')}
          </ul>
        </div>
        <div class="cons-section">
          <div class="cons-header"><span class="icon">✗</span> Cons</div>
          <ul class="cons-list">
            ${cons.map(c => `<li>${c}</li>`).join('')}
          </ul>
        </div>
      </div>`;
    editor.chain().focus().insertContent(html).run();
    setShowProsConsModal(false);
    setProsConsData({ pros: ['', '', ''], cons: ['', '', ''] });
  };

  // Insert Product Card
  const insertProductCard = () => {
    if (!editor || !productData.name) return;
    const discount = productData.originalPrice ? 
      Math.round((1 - parseFloat(productData.price.replace(/[^0-9.]/g, '')) / parseFloat(productData.originalPrice.replace(/[^0-9.]/g, ''))) * 100) : 0;
    
    const html = `
      <div class="product-card" contenteditable="false">
        ${productData.badge ? `<div class="product-badge">${productData.badge}</div>` : ''}
        ${productData.image ? `<img src="${productData.image}" alt="${productData.name}" class="product-image" />` : ''}
        <div class="product-info">
          <h4 class="product-name">${productData.name}</h4>
          <div class="product-rating">⭐ ${productData.rating}/5</div>
          <div class="product-price">
            <span class="current-price">${productData.price}</span>
            ${productData.originalPrice ? `<span class="original-price">${productData.originalPrice}</span>` : ''}
            ${discount > 0 ? `<span class="discount-badge">-${discount}%</span>` : ''}
          </div>
          <a href="${productData.url}" target="_blank" rel="noopener noreferrer nofollow" class="product-cta">
            Check Price →
          </a>
        </div>
      </div>`;
    editor.chain().focus().insertContent(html).run();
    setShowProductModal(false);
    setProductData({ name: '', price: '', originalPrice: '', image: '', url: '', rating: '4.5', badge: 'Best Value' });
  };

  // Insert Comparison Table
  const insertComparisonTable = () => {
    if (!editor) return;
    const html = `
      <div class="comparison-table-wrapper" contenteditable="false">
        <table class="comparison-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Product A</th>
              <th>Product B</th>
              <th>Product C</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Price</strong></td>
              <td>$XX</td>
              <td>$XX</td>
              <td>$XX</td>
            </tr>
            <tr>
              <td><strong>Rating</strong></td>
              <td>⭐ 4.5/5</td>
              <td>⭐ 4.2/5</td>
              <td>⭐ 4.0/5</td>
            </tr>
            <tr>
              <td><strong>Best For</strong></td>
              <td>Budget</td>
              <td>Performance</td>
              <td>Value</td>
            </tr>
            <tr class="cta-row">
              <td><strong>Buy</strong></td>
              <td><a href="#" class="table-cta">Check Price</a></td>
              <td><a href="#" class="table-cta">Check Price</a></td>
              <td><a href="#" class="table-cta">Check Price</a></td>
            </tr>
          </tbody>
        </table>
      </div>`;
    editor.chain().focus().insertContent(html).run();
    setShowCompareModal(false);
  };

  // Insert Deal Highlight
  const insertDealHighlight = () => {
    if (!editor) return;
    const html = `
      <div class="deal-highlight" contenteditable="false">
        <div class="deal-badge">🔥 HOT DEAL</div>
        <div class="deal-content">
          <h4 class="deal-title">Product Name - Limited Time Offer</h4>
          <p class="deal-description">Get this amazing product at an unbeatable price. Limited stock available!</p>
          <div class="deal-price">
            <span class="deal-current">$XX.XX</span>
            <span class="deal-original">$XX.XX</span>
          </div>
          <a href="#" target="_blank" rel="noopener noreferrer nofollow" class="deal-cta">
            Grab This Deal →
          </a>
        </div>
      </div>`;
    editor.chain().focus().insertContent(html).run();
    setShowBlocksModal(false);
  };

  if (!editor) {
    return <div className="border-2 border-gray-200 rounded-xl p-4 min-h-[500px] animate-pulse bg-gray-50" />;
  }

  const text = editor.getText();
  const charCount = text.length;
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div className="rich-text-editor border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm" data-testid="rich-text-editor">
      {/* Main Toolbar */}
      <div className="editor-toolbar border-b border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap gap-1 items-center">
          {/* Undo/Redo */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} icon={<Undo className="w-4 h-4" />} title="Undo" />
            <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} icon={<Redo className="w-4 h-4" />} title="Redo" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Headings */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} icon={<span className="font-bold text-sm">H1</span>} title="Heading 1" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} icon={<span className="font-bold text-sm">H2</span>} title="Heading 2" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} icon={<span className="font-bold text-sm">H3</span>} title="Heading 3" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} active={editor.isActive('heading', { level: 4 })} icon={<span className="font-bold text-sm">H4</span>} title="Heading 4" />
            <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} icon={<Type className="w-4 h-4" />} title="Normal Text" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Text Formatting */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} icon={<Bold className="w-4 h-4" />} title="Bold" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} icon={<Italic className="w-4 h-4" />} title="Italic" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} icon={<UnderlineIcon className="w-4 h-4" />} title="Underline" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} icon={<Strikethrough className="w-4 h-4" />} title="Strikethrough" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Alignment */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} icon={<AlignLeft className="w-4 h-4" />} title="Align Left" />
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} icon={<AlignCenter className="w-4 h-4" />} title="Align Center" />
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} icon={<AlignRight className="w-4 h-4" />} title="Align Right" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Lists */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} icon={<List className="w-4 h-4" />} title="Bullet List" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} icon={<ListOrdered className="w-4 h-4" />} title="Numbered List" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Blocks */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} icon={<Quote className="w-4 h-4" />} title="Blockquote" />
            <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} icon={<Code className="w-4 h-4" />} title="Code Block" />
            <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} icon={<Minus className="w-4 h-4" />} title="Horizontal Line" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Media */}
          <ToolbarGroup>
            <ToolbarButton onClick={setLink} active={editor.isActive('link')} icon={<LinkIcon className="w-4 h-4" />} title="Insert Link" />
            <ToolbarButton onClick={addImage} icon={<ImageIcon className="w-4 h-4" />} title="Insert Image (with crop)" />
            <ToolbarButton onClick={insertTable} icon={<TableIcon className="w-4 h-4" />} title="Insert Table" />
          </ToolbarGroup>

          <ToolbarDivider />

          {/* Affiliate Blocks */}
          <ToolbarGroup>
            <ToolbarButton onClick={() => setShowCtaModal(true)} icon={<MousePointer2 className="w-4 h-4" />} title="CTA Button" className="!bg-brand-red !text-white hover:!bg-red-700" />
            <ToolbarButton onClick={() => setShowProsConsModal(true)} icon={<ThumbsUp className="w-4 h-4" />} title="Pros & Cons" className="!bg-brand-green !text-white hover:!bg-green-700" />
            <ToolbarButton onClick={() => setShowProductModal(true)} icon={<ShoppingCart className="w-4 h-4" />} title="Product Card" className="!bg-orange-500 !text-white hover:!bg-orange-600" />
            <ToolbarButton onClick={insertComparisonTable} icon={<LayoutGrid className="w-4 h-4" />} title="Comparison Table" className="!bg-purple-500 !text-white hover:!bg-purple-600" />
            <ToolbarButton onClick={insertDealHighlight} icon={<Sparkles className="w-4 h-4" />} title="Deal Highlight" className="!bg-yellow-500 !text-gray-900 hover:!bg-yellow-400" />
          </ToolbarGroup>
        </div>
      </div>

      {/* Bubble Menu */}
      {editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }} className="bg-gray-900 rounded-lg shadow-xl border border-gray-700 flex gap-1 p-1">
          <BubbleButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}><Bold className="w-4 h-4" /></BubbleButton>
          <BubbleButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}><Italic className="w-4 h-4" /></BubbleButton>
          <BubbleButton onClick={setLink} active={editor.isActive('link')}><LinkIcon className="w-4 h-4" /></BubbleButton>
        </BubbleMenu>
      )}

      {/* Editor Content */}
      <div className="editor-wrapper">
        <EditorContent editor={editor} />
      </div>

      {/* Character/Word Count */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 flex justify-between text-sm text-gray-500">
        <span>{charCount} characters</span>
        <span>{wordCount} words</span>
        <span>~{Math.ceil(wordCount / 200)} min read</span>
      </div>

      {/* Image Cropper Modal */}
      {showCropModal && cropImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-3xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2"><Crop className="w-5 h-5" /> Crop Image (16:9)</h3>
              <button onClick={() => setShowCropModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="relative h-[400px] bg-gray-900">
              <Cropper image={cropImage} crop={crop} zoom={zoom} aspect={16/9} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <div className="p-4 flex justify-between items-center border-t">
              <div className="flex items-center gap-4">
                <label className="text-sm text-gray-600">Zoom:</label>
                <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} className="w-32" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowCropModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCropConfirm} className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Apply Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Button Modal */}
      {showCtaModal && (
        <Modal title="Insert Call-to-Action Button" onClose={() => setShowCtaModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Text *</label>
              <input type="text" value={ctaData.text} onChange={(e) => setCtaData({ ...ctaData, text: e.target.value })} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-brand-red" placeholder="e.g., Buy Now, Get Deal" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate URL *</label>
              <input type="url" value={ctaData.url} onChange={(e) => setCtaData({ ...ctaData, url: e.target.value })} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-brand-red" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Button Style</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(CTA_STYLES).map(([key, style]) => (
                  <button key={key} type="button" onClick={() => setCtaData({ ...ctaData, style: key })}
                    className={`p-3 rounded-lg border-2 transition-all ${ctaData.style === key ? 'border-brand-red' : 'border-gray-200'}`}>
                    <div className="h-8 rounded flex items-center justify-center text-sm font-semibold"
                      style={{ background: style.bg, color: style.text, border: style.border ? `2px solid ${style.border}` : 'none' }}>
                      {style.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={ctaData.newTab} onChange={(e) => setCtaData({ ...ctaData, newTab: e.target.checked })} className="w-4 h-4 text-brand-red rounded" />
              <span className="text-sm text-gray-700">Open in new tab</span>
            </label>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowCtaModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={insertCtaButton} disabled={!ctaData.text || !ctaData.url} className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Insert Button</button>
          </div>
        </Modal>
      )}

      {/* Pros/Cons Modal */}
      {showProsConsModal && (
        <Modal title="Insert Pros & Cons Box" onClose={() => setShowProsConsModal(false)}>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-green-600 mb-2 flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> Pros</label>
              {prosConsData.pros.map((pro, i) => (
                <input key={i} type="text" value={pro} onChange={(e) => {
                  const newPros = [...prosConsData.pros];
                  newPros[i] = e.target.value;
                  setProsConsData({ ...prosConsData, pros: newPros });
                }} className="w-full px-3 py-2 border border-gray-200 rounded-lg mb-2 focus:outline-none focus:border-green-500" placeholder={`Pro ${i + 1}`} />
              ))}
              <button type="button" onClick={() => setProsConsData({ ...prosConsData, pros: [...prosConsData.pros, ''] })} className="text-sm text-green-600 hover:underline">+ Add more</button>
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2 flex items-center gap-1"><ThumbsDown className="w-4 h-4" /> Cons</label>
              {prosConsData.cons.map((con, i) => (
                <input key={i} type="text" value={con} onChange={(e) => {
                  const newCons = [...prosConsData.cons];
                  newCons[i] = e.target.value;
                  setProsConsData({ ...prosConsData, cons: newCons });
                }} className="w-full px-3 py-2 border border-gray-200 rounded-lg mb-2 focus:outline-none focus:border-red-500" placeholder={`Con ${i + 1}`} />
              ))}
              <button type="button" onClick={() => setProsConsData({ ...prosConsData, cons: [...prosConsData.cons, ''] })} className="text-sm text-red-600 hover:underline">+ Add more</button>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowProsConsModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={insertProsConsBox} className="px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-green-700">Insert Pros/Cons</button>
          </div>
        </Modal>
      )}

      {/* Product Card Modal */}
      {showProductModal && (
        <Modal title="Insert Product Card" onClose={() => setShowProductModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input type="text" value={productData.name} onChange={(e) => setProductData({ ...productData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Sony WF-1000XM5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                <input type="text" value={productData.badge} onChange={(e) => setProductData({ ...productData, badge: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Best Value" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Price *</label>
                <input type="text" value={productData.price} onChange={(e) => setProductData({ ...productData, price: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="$299" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                <input type="text" value={productData.originalPrice} onChange={(e) => setProductData({ ...productData, originalPrice: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="$349" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <input type="text" value={productData.rating} onChange={(e) => setProductData({ ...productData, rating: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="4.5" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Image URL</label>
              <input type="url" value={productData.image} onChange={(e) => setProductData({ ...productData, image: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate URL *</label>
              <input type="url" value={productData.url} onChange={(e) => setProductData({ ...productData, url: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="https://..." />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowProductModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={insertProductCard} disabled={!productData.name || !productData.price || !productData.url} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">Insert Product Card</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Helper Components
const ToolbarGroup = ({ children }) => <div className="flex items-center gap-0.5 bg-white rounded-lg border border-gray-200 p-0.5">{children}</div>;
const ToolbarDivider = () => <div className="w-px h-6 bg-gray-200 mx-1" />;
const ToolbarButton = ({ onClick, disabled, active, icon, title, className = '' }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    className={`p-2 rounded-md transition-all text-gray-600 ${active ? 'bg-brand-red text-white' : 'hover:bg-gray-100'} ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
    {icon}
  </button>
);
const BubbleButton = ({ onClick, active, children }) => (
  <button type="button" onClick={onClick} className={`p-2 rounded transition-colors ${active ? 'bg-white text-gray-900' : 'text-white hover:bg-gray-700'}`}>{children}</button>
);
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
    <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
      <h3 className="text-xl font-bold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  </div>
);

export default RichTextEditor;
